import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import DataTable from "../../components/ui/DataTable";
import { Num } from "../../components/ui";
import { tokens } from "../../theme";
import { formatCurrencyManual } from "../../utils/formatters";
import { ErrorModal, ConfirmModal, SuccessModal } from "../../components/ModalAlert";
import { orderObjectStatus } from "../../constants/orderStatus";
import { getOrderStatusStyle } from "../../constants/statusStyles";
import RowAction, { RowActions, RowActionSpacer } from "../../components/ui/RowAction";
import { IconEye, IconMail } from "../../components/ui/icons";
import moment from "moment";
import { LoaderSpinner } from "../../components/spinner/loader";
import EmailIcon from '@mui/icons-material/Email';
import { Tooltip } from "@mui/material";
import { jsPDF } from "jspdf";
import axios from "axios";
import { generatePDF } from "./clientSpecificOrders";
import { currencyValues } from "../../constants/paymentValues";
import { useOptimizedQueries } from "../../hooks/useOptimizedQueries";
import { sendEmailEndpoint } from "../../apiRequests/endpoints";

const AdminClientsScreen = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientFilter, setClientFilter] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const { getClientsBasicData: getClientsBasicDataFromHook, getFilesOptimized } = useOptimizedQueries();

  const getFiles = async (bucketName, orderId) => {
    let files = [];
    const { data } = await supabase.storage.from(bucketName).list(`${orderId}/`);
    if (data) {
      data.forEach((file) => {
        const { data } = supabase.storage
          .from(bucketName)
          .getPublicUrl(`${orderId}/${file.name}`);
        files.push(data.publicUrl);
      });
    }
    return files;
  };

  // Función optimizada para cargar datos básicos de clientes primero
  const loadClientsBasicData = useCallback(async () => {
    setLoading(true);
    try {
      // Usar el hook optimizado para obtener datos básicos
      const clientsData = await getClientsBasicDataFromHook();

      // Calcular datos básicos sin cargar detalles completos
      const clientsWithBasicData = clientsData.map((client) => {
        const finishedOrders = client.orders.filter(
          (order) => order.status === orderObjectStatus.terminado
        );
        const readyToBillOrders = client.orders.filter(
          (order) => order.status === orderObjectStatus.listoParaFacturar
        );

        const finalPriceWithoutIva = readyToBillOrders
          .reduce((sum, item) => sum + (item.subTotal || 0), 0)
          .toFixed(2);

        const finalPriceWithIva = readyToBillOrders
          .reduce((sum, item) => {
            return sum + ((item.subTotal || 0) + (item.iva105 || 0) + (item.iva210 || 0));
          }, 0)
          .toFixed(2);

        const finalPriceIva105 = readyToBillOrders
          .reduce((sum, item) => sum + (item.iva105 || 0), 0)
          .toFixed(2);

        const finalPriceIva21 = readyToBillOrders
          .reduce((sum, item) => sum + (item.iva210 || 0), 0)
          .toFixed(2);

        return {
          ...client,
          countOfFinishedOrders: finishedOrders.length,
          countOfReadyToBillOrders: readyToBillOrders.length,
          finalPriceWithoutIva,
          finalPriceWithIva,
          finalPriceIva105,
          finalPriceIva21,
          orders: client.orders, // Mantener órdenes básicas para uso posterior
        };
      });

      setClients(clientsWithBasicData);
      setInitialDataLoaded(true);
    } catch (error) {
      ErrorModal({
        title: error.message,
      });
    } finally {
      setLoading(false);
    }
  }, [getClientsBasicDataFromHook]);

  // Función para cargar datos completos en segundo plano
  const loadCompleteData = useCallback(async () => {
    if (!initialDataLoaded) return;

    setLoadingDetails(true);
    try {
      // Obtener los clientes actuales para evitar dependencias circulares
      const currentClients = clients;
      
      const clientsWithCompleteData = await Promise.all(
        currentClients.map(async (client) => {
          // Cargar detalles de órdenes solo para órdenes listas para facturar
          const readyToBillOrders = client.orders.filter(
            (order) => order.status === orderObjectStatus.listoParaFacturar
          );

          const ordersWithDetails = await Promise.all(
            readyToBillOrders.map(async (order) => {
              const { data: orderDetailsData, error: detailsError } = await supabase
                .from("orderDetails")
                .select("*")
                .eq("orderId", order.orderId);

              if (detailsError) {
                console.error(`Error obteniendo detalles para orden ${order.orderId}:`, detailsError);
                return { ...order, orderDetails: [] };
              }

              return {
                ...order,
                orderDetails: orderDetailsData || []
              };
            })
          );

          // Cargar archivos solo para las órdenes que se mostrarán usando el hook optimizado
          const ordersWithFiles = await Promise.all(
            ordersWithDetails.map(async (order) => {
              const date = moment(order.createdAt).format("DD/MM/YY");
              const dateObject = new Date(order.createdAt);
              const hours = dateObject.getHours();
              const minutes = dateObject.getMinutes();
              const seconds = dateObject.getSeconds();

              let files = await getFilesOptimized(order.bucketId, order.orderId);
              return {
                ...order,
                description: order?.orderDetails[0]?.description || "Sin descripción",
                date,
                time: `${hours}:${minutes}:${seconds}`,
                files,
              };
            })
          );

          return {
            ...client,
            orders: ordersWithFiles,
          };
        })
      );

      setClients(clientsWithCompleteData);
    } catch (error) {
      console.error("Error cargando datos completos:", error);
    } finally {
      setLoadingDetails(false);
    }
  }, [initialDataLoaded, getFilesOptimized]);

  const handleSendOrdersSummary = async (client) => {
    try {
      setLoading(true);

      // Validar que el cliente tenga email
      if (!client.email || client.email.trim() === '') {
        throw new Error("El cliente no tiene un email configurado. Por favor, actualice los datos del cliente.");
      }

      // Validar formato de email principal
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(client.email)) {
        throw new Error(`El email del cliente (${client.email}) no tiene un formato válido.`);
      }

      // Validar email secundario si existe
      if (client.secondaryEmailBilling && client.secondaryEmailBilling.trim() !== '' && !emailRegex.test(client.secondaryEmailBilling)) {
        throw new Error(`El email secundario (${client.secondaryEmailBilling}) no tiene un formato válido.`);
      }

      // Primero obtenemos las órdenes sin detalles para evitar duplicaciones
      const { data: ordersData, error } = await supabase
        .from("orders")
        .select("*")
        .eq("clientId", client.clientId)
        .eq("status", orderObjectStatus.listoParaFacturar);

      if (error) throw error;

      // Verificar que hay órdenes para enviar
      if (!ordersData || ordersData.length === 0) {
        throw new Error("No hay órdenes listas para facturar para este cliente.");
      }

      // Luego obtenemos los detalles de cada orden por separado
      const ordersWithDetails = await Promise.all(
        ordersData.map(async (order) => {
          const { data: orderDetailsData, error: detailsError } = await supabase
            .from("orderDetails")
            .select("*")
            .eq("orderId", order.orderId);

          if (detailsError) {
            console.error(`Error obteniendo detalles para orden ${order.orderId}:`, detailsError);
            return { ...order, orderDetails: [] };
          }

          return {
            ...order,
            orderDetails: orderDetailsData || []
          };
        })
      );

      if (error) throw error;

      // Generamos ambos PDFs
      const summaryPdfBase64 = await generateOrdersSummaryPDF(ordersWithDetails);
      const detailedPdfBase64 = await generatePDF(ordersWithDetails);

      // Validar tamaño de los PDFs (límite de ~20MB para attachments)
      const summarySize = summaryPdfBase64.length * 0.75 / 1024 / 1024; // Convertir de base64 a MB aproximado
      const detailedSize = detailedPdfBase64.length * 0.75 / 1024 / 1024;
      const totalSize = summarySize + detailedSize;

      if (totalSize > 18) { // Dejamos margen de seguridad (límite es 20MB)
        throw new Error(
          `Los archivos PDF son demasiado grandes para enviar por email (${totalSize.toFixed(2)} MB). ` +
          `El límite es de 20 MB. Por favor, contacte al cliente directamente o reduzca el número de órdenes.`
        );
      }

      // Extraer el contenido base64 (remover el prefijo data:application/pdf;base64, si existe)
      let summaryContent = summaryPdfBase64;
      let detailedContent = detailedPdfBase64;
      if (summaryPdfBase64.includes(',')) {
        summaryContent = summaryPdfBase64.split(",")[1];
      }
      if (detailedPdfBase64.includes(',')) {
        detailedContent = detailedPdfBase64.split(",")[1];
      }

      // Preparar destinatarios principales
      const toRecipients = [
        {
          email: client.email,
          name: client.company,
        },
      ];

      // Agregar email secundario si existe y es válido
      if (client.secondaryEmailBilling && client.secondaryEmailBilling.trim() !== '') {
        toRecipients.push({
          email: client.secondaryEmailBilling.trim(),
          name: `${client.company} (Email Secundario)`,
        });
      }

      const emailData = {
        replyTo: {
          email: "dpaez@support-ti.com.ar",
          name: "Daniel Paez"
        },
        to: toRecipients,
        bcc: [
          {
            email: "facturas@support-ti.com.ar",
            name: "Facturas Support TI",
          },
        ],
        subject: "Detalle de Ordenes de Trabajo",
        htmlContent: `<html><body><p>Estimado ${client.company}</p><p>A continuación adjuntamos el detalle de sus órdenes pendientes de facturación en dos formatos diferentes.</p><br><p>Atte.</p><p>Equipo Support TI</p></body></html>`,
        attachment: [
          {
            content: summaryContent,
            name: "resumen_ordenes.pdf",
          },
          {
            content: detailedContent,
            name: "ordenes_pendientes.pdf",
          },
        ],
      };

      const response = await axios.post(
        sendEmailEndpoint,
        emailData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || "Error desconocido al enviar el email");
      }

      SuccessModal({ 
        title: "Enviado!", 
        text: "El resumen de órdenes ha sido enviado correctamente" 
      });
    } catch (error) {
      // Capturar información adicional del error de axios
      let errorMessage = error.message;
      if (error.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data && error.response.data.code) {
          errorMessage = `Error ${error.response.data.code}: ${JSON.stringify(error.response.data)}`;
        }
      }
      
      ErrorModal({ 
        title: "Error al enviar el resumen", 
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const showSendEmailConfirmation = (client) => {
    ConfirmModal({
      title: "Enviar resumen de órdenes",
      text: "¿Desea enviar al cliente un detalle de las ordenes listas para facturar?",
      confirmText: "Aceptar",
      cancelText: "Cancelar",
      onConfirm: () => handleSendOrdersSummary(client)
    });
  };

  const generateOrdersSummaryPDF = async (orders) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const text = "Resumen de Órdenes Listas para Facturar";
    doc.setFontSize(16);

    const textWidth = doc.getTextWidth(text);
    const xPosition = (pageWidth - textWidth) / 2;
    doc.setFont("helvetica", "bold");
    doc.text(text, xPosition, 20);
    doc.setFont("helvetica", "normal");
    let yPosition = 40;

    // Totales generales
    let totalIva105 = 0;
    let totalIva210 = 0;
    let totalSubTotal = 0;
    let totalOrder = 0;

    for (const order of orders) {
      let totalOrder = 0;
      let totalOrderIva = 0;
      
      // Encabezado de la orden
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const date = moment(order.createdAt).format("DD/MM/YY");
      doc.text(`Orden #${order.orderId} - Fecha: ${date}`, 20, yPosition);
      yPosition += 10;

      //Titulos de la tabla
      doc.text("Cant.", 20, yPosition);
      doc.text("Descripción", 35, yPosition);
      doc.text("Precio", 135, yPosition);
      doc.text("Subtotal", 170, yPosition);
      yPosition += 8;

      // Detalles de la orden
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      for (const detail of order.orderDetails) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        const currencyMultiplier = detail.currency !== currencyValues[0].value ? parseFloat(order.currencyRate) : 1;
        const subTotal = detail.price * detail.quantity * currencyMultiplier;
        
        // Calcular IVA para este detalle
        const ivaPercentage = parseFloat(detail.ivaPercentage);
        let detailIva = 0;
        if (ivaPercentage === 10.5) detailIva = subTotal * 0.105;
        if (ivaPercentage === 21) detailIva = subTotal * 0.21;
        
        totalOrder += subTotal;
        totalOrderIva += detailIva;
        
        doc.text(`${detail.quantity}`, 22, yPosition);
        doc.text(`${detail.description.substring(0, 40)}`, 35, yPosition);
        doc.text(`$ ${detail.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 135, yPosition);
        doc.text(`$ ${subTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 170, yPosition);
        yPosition += 8;

        // Acumular totales generales
        if (ivaPercentage === 10.5) totalIva105 += detailIva;
        if (ivaPercentage === 21) totalIva210 += detailIva;
        
        totalSubTotal += subTotal;
      }

      // Tabla de pie de orden
      yPosition += 10;
      doc.text(`Cotización: $ ${order.currencyRate.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 20, yPosition);
      doc.text(`IVA 10,5%: $ ${order.iva105.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0,00'}`, 60, yPosition);
      doc.text(`IVA 21%: $ ${order.iva210.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0,00'}`, 110, yPosition);
      const totalOrderWithIva = totalOrder + totalOrderIva;
      doc.text(`TOTAL: $ ${totalOrderWithIva.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 155, yPosition);
      yPosition += 10;
      
      doc.text("_".repeat(90), 20, yPosition);
      yPosition += 10;
    }

    // Totales generales
    const totalGeneral = totalSubTotal + totalIva105 + totalIva210;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Totales Generales", 20, yPosition);
    yPosition += 8;
    doc.text(`IVA 10,5%: $ ${totalIva105.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 20, yPosition);
    yPosition += 5;
    doc.text(`IVA 21%: $ ${totalIva210.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 20, yPosition);
    yPosition += 5;
    doc.text(`Subtotal: $ ${totalSubTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 20, yPosition);
    yPosition += 3;
    doc.text("_".repeat(30), 20, yPosition);
    yPosition += 5;
    doc.text(`Total General: $ ${totalGeneral.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 20, yPosition);
    yPosition += 8;

    return doc.output("datauristring");
  };

  useEffect(() => {
    loadClientsBasicData();
  }, [loadClientsBasicData]);

  useEffect(() => {
    loadCompleteData();
  }, [loadCompleteData]);

  // Búsqueda y badges del lado del cliente: los datos ya están todos en memoria.
  const term = clientSearch.trim().toLowerCase();

  const visibleClients = clients.filter((client) => {
    if (clientFilter === "terminadas" && !(client.countOfFinishedOrders > 0)) return false;
    if (clientFilter === "facturar" && !(client.countOfReadyToBillOrders > 0)) return false;
    if (!term) return true;
    return [client.company, client.name, client.lastName, client.email, client.cuit]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  const clientChips = [
    {
      id: "todos",
      label: "Todos",
      active: clientFilter === null,
      onClick: () => setClientFilter(null),
    },
    {
      id: "terminadas",
      label: `Con terminadas (${clients.filter((c) => c.countOfFinishedOrders > 0).length})`,
      dot: getOrderStatusStyle(orderObjectStatus.terminado).dot,
      active: clientFilter === "terminadas",
      onClick: () => setClientFilter(clientFilter === "terminadas" ? null : "terminadas"),
    },
    {
      id: "facturar",
      label: `Para facturar (${clients.filter((c) => c.countOfReadyToBillOrders > 0).length})`,
      dot: getOrderStatusStyle(orderObjectStatus.listoParaFacturar).dot,
      active: clientFilter === "facturar",
      onClick: () => setClientFilter(clientFilter === "facturar" ? null : "facturar"),
    },
  ];

  /**
   * Celda numérica: angosta, alineada a la derecha y con cifras tabulares.
   *
   * El encabezado envuelve en dos líneas. Antes no lo hacía, así que cada
   * columna quedaba tan ancha como su título ("Para facturar" empujaba 116 px)
   * y la columna de Acciones se iba fuera de la pantalla.
   */
  const numericCell = (width) => ({
    setCellProps: () => ({ style: { width, textAlign: "right", whiteSpace: "nowrap" } }),
    setCellHeaderProps: () => ({
      style: { width, textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 },
    }),
  });

  const columns = [
    {
      // Empresa, contacto y email en una sola celda: antes eran tres columnas
      // anchas para tres datos cortos.
      name: "company",
      label: "Cliente",
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const item = visibleClients[dataIndex];
          const contacto = [item.name, item.lastName].filter(Boolean).join(" ");
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <Box component="span" sx={{ fontSize: "13.5px", fontWeight: 500 }}>
                {item.company || "Sin razón social"}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "11.5px",
                  color: tokens.ink3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {[contacto, item.email].filter(Boolean).join(" · ") || "Sin contacto"}
              </Box>
            </Box>
          );
        },
      },
    },
    { name: "name", label: "Nombre", options: { filter: false, sort: true, display: false } },
    { name: "lastName", label: "Apellido", options: { filter: false, sort: true, display: false } },
    { name: "email", label: "Email", options: { filter: false, sort: true, display: false } },
    {
      name: "countOfFinishedOrders",
      label: "Terminadas",
      options: {
        filter: false,
        sort: true,
        ...numericCell(72),
        customBodyRenderLite: (dataIndex) => {
          const value = visibleClients[dataIndex].countOfFinishedOrders;
          return (
            <Num sx={{ fontSize: "13px", color: value ? tokens.ink : tokens.ink4 }}>{value}</Num>
          );
        },
      },
    },
    {
      name: "countOfReadyToBillOrders",
      label: "Para facturar",
      options: {
        filter: false,
        sort: true,
        ...numericCell(76),
        customBodyRenderLite: (dataIndex) => {
          const value = visibleClients[dataIndex].countOfReadyToBillOrders;
          if (!value) return <Num sx={{ fontSize: "13px", color: tokens.ink4 }}>0</Num>;
          // Lo que se puede facturar es la acción de esta pantalla: se destaca.
          return (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 26,
                padding: "2px 8px",
                borderRadius: 999,
                fontSize: "12px",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                color: "#45499b",
                backgroundColor: "#eceef8",
              }}
            >
              {value}
            </Box>
          );
        },
      },
    },
    {
      name: "finalPriceIva105",
      label: "IVA 10,5%",
      options: {
        filter: false,
        sort: true,
        // Ocultas de entrada: con las dos de IVA la tabla se iba 154 px fuera
        // del contenedor y Acciones quedaba detrás del scroll horizontal. El
        // desglose sigue disponible en "Ver columnas" y en la exportación.
        display: false,
        ...numericCell(116),
        customBodyRenderLite: (dataIndex) => (
          <Num sx={{ fontSize: "13px", color: tokens.ink2 }}>
            {formatCurrencyManual(visibleClients[dataIndex].finalPriceIva105)}
          </Num>
        ),
      },
    },
    {
      name: "finalPriceIva21",
      label: "IVA 21%",
      options: {
        filter: false,
        sort: true,
        display: false,
        ...numericCell(116),
        customBodyRenderLite: (dataIndex) => (
          <Num sx={{ fontSize: "13px", color: tokens.ink2 }}>
            {formatCurrencyManual(visibleClients[dataIndex].finalPriceIva21)}
          </Num>
        ),
      },
    },
    {
      name: "finalPriceWithoutIva",
      label: "Subtotal",
      options: {
        filter: false,
        sort: true,
        ...numericCell(124),
        customBodyRenderLite: (dataIndex) => (
          <Num sx={{ fontSize: "13px", color: tokens.ink2 }}>
            {formatCurrencyManual(visibleClients[dataIndex].finalPriceWithoutIva)}
          </Num>
        ),
      },
    },
    {
      name: "finalPriceWithIva",
      label: "Total",
      options: {
        filter: false,
        sort: true,
        ...numericCell(132),
        customBodyRenderLite: (dataIndex) => (
          <Num sx={{ fontSize: "13.5px", fontWeight: 600 }}>
            {formatCurrencyManual(visibleClients[dataIndex].finalPriceWithIva)}
          </Num>
        ),
      },
    },
    {
      name: "actions",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { width: 96, textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { width: 96, textAlign: "right" } }),
        customBodyRenderLite: (dataIndex) => {
          const item = visibleClients[dataIndex];
          const hasReadyToBillOrders = item.countOfReadyToBillOrders > 0;

          return (
            <RowActions>
              <RowAction
                icon={IconEye}
                title="Ver órdenes del cliente"
                onClick={() =>
                  navigate("/client-specific-orders", { state: { client: item } })
                }
              />
              {hasReadyToBillOrders ? (
                <RowAction
                  icon={IconMail}
                  title="Enviar resumen por mail"
                  onClick={() => showSendEmailConfirmation(item)}
                />
              ) : (
                <RowActionSpacer />
              )}
            </RowActions>
          );
        },
      },
    },
  ];

  const options = {
    responsive: "simple",
    sortOrder: { name: "company", direction: "asc" },
    selectableRows: "none",
    selectableRowsOnClick: true,
    rowsPerPage: 25,
    downloadOptions: {
      filename: "ordenes-por-cliente.csv",
      separator: ";",
      filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
    },
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        paddingBottom: loadingDetails ? "80px" : 0,
      }}
    >
      
      <DataTable
        data={visibleClients}
        columns={columns}
        search={{
          value: clientSearch,
          onChange: setClientSearch,
          placeholder: "Buscar por empresa, contacto, email o CUIT…",
        }}
        chips={clientChips}
        resultCount={visibleClients.length}
        totalCount={clients.length}
        emptyMessage="No se encontraron clientes"
        options={options}
      />

      {/* Indicador de progreso para carga de detalles - Al pie de la página */}
      {loadingDetails && (
        <div style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          right: '0',
          padding: '15px 20px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          zIndex: 1000,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid #f3f3f3',
            borderTop: '2px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ fontSize: '14px', color: '#6c757d' }}>
            Terminando de cargar las órdenes completas...
          </span>
        </div>
      )}
    </Box>
  );
};

export default AdminClientsScreen;
